/** @format */

import { selectUserZipCode } from '@state/data-access';

import {
	Component,
	ComponentRef,
	effect,
	inject,
	PLATFORM_ID,
	TemplateRef,
	viewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { isPlatformBrowser, NgClass, AsyncPipe } from '@angular/common';
import { take } from 'rxjs/operators';
import { HomrDataService } from '@shared/homr/data-access';
import { selectSameDayDeliveryButtonText, selectShipMode } from '@pdp/add-to-cart/data-access';
import {
	HmfDrawerComponent,
	HmfModalFactoryService,
	HmfActionTextComponent
} from '@dsg-tech/homefield-angular';
import { DynamicDrawerSizes, ProductSelectionUtilityService } from '@pdp/utils';
import { AnalyticsService, BreakpointsService } from '@shared/data-access/services';
import { ChangeZipCodeButtonComponent } from '../../../change-zip-code-button/change-zip-code-button.component';

const INFORMATIONAL_CONTENT_KEY = 'Informationals Content';

interface SameDayDeliveryContent {
	content?: {
		[INFORMATIONAL_CONTENT_KEY]: { wysiwyg: string }[];
	};
}

@Component({
	selector: 'pdp-feature-same-day-delivery-subtext',
	templateUrl: './same-day-delivery-subtext.component.html',
	styleUrl: './same-day-delivery-subtext.component.scss',
	imports: [NgClass, ChangeZipCodeButtonComponent, HmfActionTextComponent, AsyncPipe]
})
export class SameDayDeliverySubtextComponent {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly store = inject(Store);
	private readonly homrDataService = inject(HomrDataService);
	private readonly modalFactoryService = inject(HmfModalFactoryService);
	private readonly pdslUtilityService = inject(ProductSelectionUtilityService);
	private readonly breakpointsService = inject(BreakpointsService);
	private readonly analyticsService = inject(AnalyticsService);

	/**
	 * @description selects shipMode from fulfillment slice of state.
	 * This is used to determine the selected CSS state of the fulfillment button and
	 * is passed to the analytics event so they know which method has been chosen.
	 */
	shipMode$ = this.store.select(selectShipMode);

	/**
	 * @description retrieves the user's zipcode from the state
	 */
	zipCode$ = this.store.select(selectUserZipCode);

	/**
	 * @description state selector returns an object that has the information to display for the fulfillment button and button label
	 */
	sameDayDeliveryButtonText$ = this.store.select(selectSameDayDeliveryButtonText);

	/**
	 * @description string of content that comes back from the homr service and displays detail delivery information in the side panel
	 * This content is set up in homr by the marketing team.
	 * In homr this is under the page called : Same-day-delivery-details-panel
	 */
	sameDayDeliveryContent: string;

	private drawerRef: ComponentRef<HmfDrawerComponent>;
	drawerContent = viewChild.required<TemplateRef<unknown>>('drawerContent');

	constructor() {
		effect(() => {
			const breakpoint = this.breakpointsService.breakpoint();
			if (this.drawerRef) {
				this.drawerRef.setInput(
					'size',
					this.pdslUtilityService.getValueByScreenSize(DynamicDrawerSizes, breakpoint)
				);
			}
		});
	}

	/**
	 * @description when the Details button is clicked in the button label
	 * open the drawer and make a homr service call to get the Same-day-delivery-details-panel content
	 */
	getSameDayDeliveryDetails() {
		this.openDrawer();
		this.analyticsService.triggerAnalyticsEvent('ModalOpen', {
			ModalName: 'Same Day Delivery Details'
		});
		if (isPlatformBrowser(this.platformId)) {
			this.homrDataService
				.getData$('static', 2, 'same-day-delivery-details-panel')
				.pipe(take(1))
				.subscribe((sameDayDeliveryContent: SameDayDeliveryContent) => {
					if (sameDayDeliveryContent?.content?.[INFORMATIONAL_CONTENT_KEY][0]?.wysiwyg) {
						this.sameDayDeliveryContent = decodeURIComponent(
							sameDayDeliveryContent.content[INFORMATIONAL_CONTENT_KEY][0].wysiwyg
						);
					}
				});
		}
	}

	openDrawer() {
		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: 'How Delivery Works',
					closable: true,
					mobilePeek: true,
					direction: 'right',
					isCloseButtonSticky: true,
					size: this.pdslUtilityService.getValueByScreenSize(
						DynamicDrawerSizes,
						this.breakpointsService.breakpoint()
					)
				},
				this.drawerContent()
			);
		}

		this.drawerRef.instance.openDrawer();
	}
}
